import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useSession } from "@/services/auth0/session";
import { Order } from "@/api.types";
import { z } from "zod";
import { parcelCreateSchema } from "@/api.schemas";
import { useOrderById } from "./use-order";

interface ParcelOperation {
  existingParcelId?: string;
  delete?: boolean;
  data?: {
    weight: number;
    dimensions: {
      length: number;
      width: number;
      height: number;
    };
    isFragile: boolean;
    description: string;
  };
}

export function useUpdateParcels(
  orderId: string,
  options?: {
    onSuccess?: () => void;
  }
) {
  const { token, customer } = useSession();
  const queryClient = useQueryClient();
  const { data: currentOrder } = useOrderById(orderId);

  return useMutation({
    mutationFn: async (parcels: z.infer<typeof parcelCreateSchema>[]) => {
      if (!currentOrder) {
        throw new Error("Order not found");
      }

      // Create operations array for batch update
      const operations: ParcelOperation[] = parcels.map((parcel, index) => {
        const existingParcel = currentOrder.parcels[index];

        // If we have an existing parcel, it's either an update or delete
        if (existingParcel) {
          return {
            existingParcelId: existingParcel.id,
            data: {
              weight: Number(parcel.weight) || 0,
              dimensions: {
                length: Number(parcel.dimensions?.length) || 0,
                width: Number(parcel.dimensions?.width) || 0,
                height: Number(parcel.dimensions?.height) || 0,
              },
              isFragile: Boolean(parcel.isFragile),
              description: parcel.description ?? "",
            },
          };
        }

        // If no existing parcel, it's a new one
        return {
          data: {
            weight: Number(parcel.weight) || 0,
            dimensions: {
              length: Number(parcel.dimensions?.length) || 0,
              width: Number(parcel.dimensions?.width) || 0,
              height: Number(parcel.dimensions?.height) || 0,
            },
            isFragile: Boolean(parcel.isFragile),
            description: parcel.description ?? "",
          },
        };
      });

      // Add delete operations for removed parcels
      if (currentOrder.parcels.length > parcels.length) {
        const parcelsToDelete = currentOrder.parcels.slice(parcels.length);
        parcelsToDelete.forEach((parcel) => {
          operations.push({
            existingParcelId: parcel.id,
            delete: true,
          });
        });
      }

      const { data } = await axios.patch<{
        data: Order;
        metadata: any;
      }>(
        `${import.meta.env.VITE_API_URL}/api/v1/orders/${orderId}/parcels/as-customer`,
        { operations },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["order", orderId] });
      await queryClient.invalidateQueries({
        queryKey: ["customerOrders", customer?.id],
      });
      options?.onSuccess?.();
    },
  });
}
